import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';

export default function AntiguaWeather() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://la-antigua-guatemala.com';

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Antigua Guatemala Weather Guide",
    "description": "Complete Antigua Guatemala weather guide by month. Dry vs rainy season, best time for volcano hikes, what to pack, and climate tips.",
    "url": `${siteUrl}/antigua-guatemala-weather`,
    "inLanguage": "en-US",
    "datePublished": "2025-01-15",
    "dateModified": "2025-06-01",
    "author": {
      "@type": "Organization",
      "name": "La Antigua Guatemala",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "La Antigua Guatemala",
      "url": siteUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/antigua-guatemala-weather`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
      { "@type": "ListItem", "position": 2, "name": "Travel Guide", "item": `${siteUrl}/antigua-guatemala-travel-guide` },
      { "@type": "ListItem", "position": 3, "name": "Weather Guide", "item": `${siteUrl}/antigua-guatemala-weather` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the weather like in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Antigua Guatemala has spring-like weather year-round with temperatures between 60-75°F (15-24°C). The city is located at 7,500 feet elevation, which keeps it cooler than Guatemala's lowlands. There are two main seasons: dry season (November-April) and rainy season (May-October)."
        }
      },
      {
        "@type": "Question",
        "name": "When is the best time to visit Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best time to visit Antigua is during the dry season from November to April, with peak season from December to March. This period offers clear skies, perfect weather for volcano hikes, and minimal rain."
        }
      },
      {
        "@type": "Question",
        "name": "Does it rain a lot in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "During the rainy season (May-October), Antigua experiences afternoon showers that typically last 2-4 hours. Mornings are usually clear. The wettest months are September and October. During the dry season (November-April), rain is rare."
        }
      },
      {
        "@type": "Question",
        "name": "What is the dry season in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The dry season in Antigua runs from November to April. This is the best time for volcano hikes, outdoor activities, and photography. January and February are the driest months with almost no rain."
        }
      },
      {
        "@type": "Question",
        "name": "Is Antigua cold at night?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Antigua can be cool at night, especially during the dry season. Temperatures drop to around 50-60°F (10-15°C) in the evenings. Bring a light jacket or fleece for nighttime. Volcano hikes require warm layers as temperatures drop significantly at higher elevations."
        }
      },
      {
        "@type": "Question",
        "name": "What should I pack for Antigua weather?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pack layers for Antigua's variable temperatures. Bring light clothing for daytime, a jacket or fleece for cool mornings and evenings, rain jacket (any season), sunscreen, hat, and sunglasses. For volcano hikes, pack warm clothes including thermal layers, gloves, and a warm hat."
        }
      },
      {
        "@type": "Question",
        "name": "Can I hike Acatenango in rainy season?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, you can hike Acatenango during rainy season, but conditions are more challenging. Tours typically depart in the morning when weather is clearer. However, clouds may obscure views of Fuego volcano. The dry season (November-April) offers the best conditions for Acatenango hikes."
        }
      },
      {
        "@type": "Question",
        "name": "What is the temperature in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Antigua has consistent year-round temperatures averaging 60-75°F (15-24°C) during the day. Mornings and evenings are cooler at 50-60°F (10-15°C). The high elevation keeps temperatures moderate and comfortable throughout the year."
        }
      }
    ]
  };

  const relatedGuides = [
    {
      title: "Best Time to Visit Antigua Guatemala",
      path: "/best-time-to-visit-antigua-guatemala",
      description: "Detailed guide on the best months to visit Antigua based on weather, crowds, and prices"
    },
    {
      title: "Acatenango Volcano Hike",
      path: "/acatenango-hike-from-antigua",
      description: "Complete guide to hiking Acatenango with weather considerations and packing tips"
    },
    {
      title: "Things to Do in Antigua",
      path: "/things-to-do-in-antigua-guatemala",
      description: "Top attractions and activities in Antigua for every season"
    },
    {
      title: "Antigua Itineraries",
      path: "/itineraries",
      description: "Ready-made trip plans for 2, 3, and 5 days in Antigua"
    },
    {
      title: "Acatenango Packing List",
      path: "/acatenango-packing-list",
      description: "Essential gear and clothing for volcano hikes in different weather conditions"
    },
    {
      title: "Day Trips from Antigua",
      path: "/day-trips-from-antigua-guatemala",
      description: "Best excursions from Antigua with weather and timing tips"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Antigua Guatemala Weather | Month-by-Month Climate Guide"
        description="Complete Antigua Guatemala weather guide by month. Dry vs rainy season, best time for volcano hikes, what to pack, and climate tips."
        canonical={`${siteUrl}/antigua-guatemala-weather`}
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/5bdcf60adc235df8452f2f8b00ae5b26.jpg",
          width: 1200,
          height: 630
        }}
        schema={[seoSchema, breadcrumbSchema, faqSchema]}
      />
      
      <Navbar darkText />
      
      <div className="pt-20">
        <Breadcrumbs items={[
          { name: "Travel Guide", path: "/antigua-guatemala-travel-guide" },
          { name: "Weather Guide", path: "/antigua-guatemala-weather" }
        ]} />

        {/* Hero */}
        <section className="relative h-[50vh] flex items-center justify-center">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Antigua%20Guatemala%20weather%20seasons%20comparison%20split%20view%20showing%20dry%20season%20clear%20blue%20sky%20with%20Agua%20volcano%20and%20rainy%20season%20with%20dramatic%20clouds%2C%20seasonal%20climate%20contrast%2C%20travel%20planning%20photography&width=1920&height=600&seq=weather-hero&orientation=landscape"
              alt="Antigua Guatemala Weather"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Antigua Guatemala Weather Guide</h1>
            <p className="text-xl md:text-2xl">Month-by-month climate guide for planning your perfect trip</p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Understanding Antigua Guatemala's weather is essential for planning your trip. Located at 7,500 feet elevation in Guatemala's highlands, Antigua enjoys spring-like temperatures year-round but has distinct dry and rainy seasons. This complete weather guide covers month-by-month conditions, best times for volcano hikes, what to pack, and how weather affects your activities. Whether you're planning to hike Acatenango, explore colonial streets, or take day trips, knowing what weather to expect will help you pack appropriately and schedule activities for the best experience.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Antigua Guatemala Weather Overview</h2>
              <div className="bg-gray-50 p-8 rounded-lg mb-8">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <span className="ml-3 text-gray-700"><strong>Elevation:</strong> 7,500 feet (2,300 meters) - cooler than Guatemala's lowlands</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                      <i className="ri-temp-hot-line text-xl"></i>
                    </div>
                    <span className="ml-3 text-gray-700"><strong>Year-round temperatures:</strong> 60-75°F (15-24°C) during the day</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                      <i className="ri-cloudy-line text-xl"></i>
                    </div>
                    <span className="ml-3 text-gray-700"><strong>Two main seasons:</strong> Dry (November-April) and Rainy (May-October)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                      <i className="ri-moon-line text-xl"></i>
                    </div>
                    <span className="ml-3 text-gray-700"><strong>Cool mornings and evenings:</strong> 50-60°F (10-15°C)</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Dry Season (November to April)</h2>
              <p className="text-gray-700 mb-6">
                The dry season is the best time to visit Antigua Guatemala. This period offers clear skies, minimal rain, and perfect conditions for volcano hikes and outdoor activities. Peak tourist season runs from December to March, bringing higher hotel prices and larger crowds, but the weather is consistently excellent.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-teal-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Advantages</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Clear skies and excellent visibility</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Perfect for Acatenango volcano hikes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Ideal for photography</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Comfortable for walking tours</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Considerations</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Higher hotel prices</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>More tourists and crowds</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Book accommodations early</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Tours fill up quickly</span>
                    </li>
                  </ul>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Rainy Season (May to October)</h2>
              <p className="text-gray-700 mb-6">
                The rainy season brings afternoon showers that typically last 2-4 hours, but mornings are often clear and beautiful. This period offers lush green landscapes, fewer tourists, and better hotel deals. You can still enjoy most activities by planning them for the morning hours.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-teal-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Advantages</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Lower hotel prices</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Fewer tourists</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Lush, green landscapes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                      <span>Mornings usually clear</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Considerations</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Afternoon rain showers</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Clouds may obscure volcano views</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Bring rain jacket</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-alert-line text-gray-500 mt-1 mr-2"></i>
                      <span>Plan activities for mornings</span>
                    </li>
                  </ul>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Month-by-Month Weather Guide</h2>
              
              <div className="space-y-6 mb-12">
                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">January & February</h3>
                    <span className="bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">BEST WEATHER</span>
                  </div>
                  <p className="text-gray-700 mb-3">The driest and most popular months for visiting Antigua. Clear skies, perfect temperatures, and ideal conditions for all activities.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 60-75°F (15-24°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Minimal to none
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> Volcano hikes, photography
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">March & April</h3>
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold">SEMANA SANTA</span>
                  </div>
                  <p className="text-gray-700 mb-3">Dry season ending with excellent weather. Semana Santa (Holy Week) brings spectacular celebrations but massive crowds. Book months in advance.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 62-77°F (16-25°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Occasional showers late April
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> Cultural events, hiking
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">May & June</h3>
                  <p className="text-gray-700 mb-3">Rainy season begins with afternoon showers. Mornings are usually clear, making it still possible to enjoy most activities. Fewer crowds and better hotel deals.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 60-75°F (15-24°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Afternoon showers
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> Budget travel, morning activities
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">July & August</h3>
                  <p className="text-gray-700 mb-3">Mid-rainy season with a brief dry spell called "Canícula" in July. Mornings usually clear, afternoons bring rain. Good time for budget travelers.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 60-73°F (15-23°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Regular afternoon showers
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> Budget travel, fewer crowds
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">September & October</h3>
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">WETTEST MONTHS</span>
                  </div>
                  <p className="text-gray-700 mb-3">The wettest months with heaviest afternoon rains. Lowest prices and fewest tourists. Still possible to visit, but expect rain and clouds.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 60-73°F (15-23°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Heavy afternoon rains
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> Budget travel, off-season
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">November & December</h3>
                    <span className="bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">DRY SEASON RETURNS</span>
                  </div>
                  <p className="text-gray-700 mb-3">Dry season returns with excellent weather. December brings holiday crowds. Book accommodations early for Christmas and New Year.</p>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <strong className="text-gray-900">Temperature:</strong> 58-73°F (14-23°C)
                    </div>
                    <div>
                      <strong className="text-gray-900">Rain:</strong> Minimal
                    </div>
                    <div>
                      <strong className="text-gray-900">Best for:</strong> All activities, holidays
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Best Time for Volcano Hikes</h2>
              <div className="bg-gray-50 p-8 rounded-lg mb-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Acatenango Volcano</h3>
                    <p className="text-gray-700 mb-3">Best time: November to April (dry season). Clear skies provide the best views of erupting Fuego volcano. Avoid September and October when clouds often obscure views.</p>
                    <Link to="/acatenango-hike-from-antigua" className="inline-block mt-4 text-teal-500 hover:text-teal-600 font-semibold">
                      Complete Acatenango Guide →
                    </Link>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Pacaya Volcano</h3>
                    <p className="text-gray-700 mb-3">Best time: Year-round. This shorter half-day hike is possible even during rainy season. Morning departures recommended to avoid afternoon showers.</p>
                    <Link to="/pacaya-volcano-day-trip-from-antigua" className="text-teal-500 hover:text-teal-600 font-semibold">
                      Pacaya Volcano Guide →
                    </Link>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Rainy Season Hiking Tips</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                        <span>Choose morning departure times</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                        <span>Bring waterproof gear</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                        <span>Expect muddy trails</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-teal-500 mt-1 mr-2"></i>
                        <span>Views may be limited by clouds</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What to Pack for Antigua Weather</h2>
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Essential Items (Year-Round)</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-t-shirt-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Layers:</strong> Light clothing for daytime, jacket for evenings</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-umbrella-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Rain jacket:</strong> Essential even in dry season</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-sun-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Sun protection:</strong> Sunscreen, hat, sunglasses (high altitude)</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-shirt-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Light jacket or fleece:</strong> For cool mornings and evenings</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">For Volcano Hikes</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-temp-cold-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Warm layers:</strong> Thermal underwear, fleece, down jacket</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-hand-sanitizer-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Gloves and warm hat:</strong> Temperatures drop significantly at night</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-footprint-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Hiking boots:</strong> Waterproof with good ankle support</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                        <i className="ri-flashlight-line text-xl"></i>
                      </div>
                      <span className="ml-3 text-gray-700"><strong>Headlamp:</strong> Essential for overnight hikes</span>
                    </li>
                  </ul>
                  <Link to="/acatenango-packing-list" className="inline-block mt-4 text-teal-500 hover:text-teal-600 font-semibold">
                    Complete Acatenango Packing List →
                  </Link>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How Weather Affects Your Activities</h2>
              <div className="space-y-4 mb-12">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Colonial Center Exploration</h3>
                  <p className="text-gray-700">Walkable year-round. Bring umbrella during rainy season for afternoon showers. Mornings are best for photography in any season.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Volcano Hikes</h3>
                  <p className="text-gray-700">Best during dry season (November-April) for clear views. Possible year-round but more challenging in rainy season with muddy trails and limited visibility.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Day Trips</h3>
                  <p className="text-gray-700">Possible year-round. Schedule morning departures during rainy season. Chichicastenango market and Lake Atitlán visits work well in any weather.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Photography</h3>
                  <p className="text-gray-700">Clearest views and best light during dry season (November-April). Early morning offers best conditions year-round before clouds develop.</p>
                </div>
              </div>

              {/* FAQ Section */}
              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-8">Frequently Asked Questions</h2>
              <div className="space-y-6 mb-12">
                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">What is the weather like in Antigua Guatemala?</h3>
                  <p className="text-gray-700">Antigua Guatemala has spring-like weather year-round with temperatures between 60-75°F (15-24°C). The city is located at 7,500 feet elevation, which keeps it cooler than Guatemala's lowlands. There are two main seasons: dry season (November-April) and rainy season (May-October).</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">When is the best time to visit Antigua?</h3>
                  <p className="text-gray-700">The best time to visit Antigua is during the dry season from November to April, with peak season from December to March. This period offers clear skies, perfect weather for volcano hikes, and minimal rain.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Does it rain a lot in Antigua?</h3>
                  <p className="text-gray-700">During the rainy season (May-October), Antigua experiences afternoon showers that typically last 2-4 hours. Mornings are usually clear. The wettest months are September and October. During the dry season (November-April), rain is rare.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">What is the dry season in Antigua?</h3>
                  <p className="text-gray-700">The dry season in Antigua runs from November to April. This is the best time for volcano hikes, outdoor activities, and photography. January and February are the driest months with almost no rain.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Is Antigua cold at night?</h3>
                  <p className="text-gray-700">Antigua can be cool at night, especially during the dry season. Temperatures drop to around 50-60°F (10-15°C) in the evenings. Bring a light jacket or fleece for nighttime. Volcano hikes require warm layers as temperatures drop significantly at higher elevations.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">What should I pack for Antigua weather?</h3>
                  <p className="text-gray-700">Pack layers for Antigua's variable temperatures. Bring light clothing for daytime, a jacket or fleece for cool mornings and evenings, rain jacket (any season), sunscreen, hat, and sunglasses. For volcano hikes, pack warm clothes including thermal layers, gloves, and a warm hat.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Can I hike Acatenango in rainy season?</h3>
                  <p className="text-gray-700">Yes, you can hike Acatenango during rainy season, but conditions are more challenging. Tours typically depart in the morning when weather is clearer. However, clouds may obscure views of Fuego volcano. The dry season (November-April) offers the best conditions for Acatenango hikes.</p>
                </div>

                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">What is the temperature in Antigua?</h3>
                  <p className="text-gray-700">Antigua has consistent year-round temperatures averaging 60-75°F (15-24°C) during the day. Mornings and evenings are cooler at 50-60°F (10-15°C). The high elevation keeps temperatures moderate and comfortable throughout the year.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Next Steps</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/antigua-guatemala-itineraries" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Itineraries</h3>
                <p className="text-gray-600 text-sm">2, 3, and 5-day trip plans</p>
              </Link>
              <Link to="/acatenango-hike-from-antigua" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Acatenango Hike</h3>
                <p className="text-gray-600 text-sm">Complete volcano hiking guide</p>
              </Link>
              <Link to="/best-hotels-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Where to Stay</h3>
                <p className="text-gray-600 text-sm">Best hotels in Antigua</p>
              </Link>
              <Link to="/plan-my-antigua-trip" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Get Custom Recommendations</h3>
                <p className="text-gray-600">Personalized trip planning assistance</p>
              </Link>
            </div>
          </div>
        </section>

        <RelatedGuides guides={relatedGuides} />

        {/* CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready To Plan Your Antigua Trip?</h2>
            <p className="text-xl text-white/90 mb-8">Get personalized recommendations based on your travel dates and weather preferences.</p>
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
              Get Custom Plan
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
